// palletApi.js
// Supabase-backed utilities for pallet image upload, classification, history listing, deletion.
// Classification uses a placeholder (client-side mock) until Edge Function is deployed.

import supabase, { SUPABASE_ENABLED } from './supabaseClient';

// In-memory fallback when Supabase not configured
const memory = {
  images: [],
  classifications: [],
};
let memImageId = 1;
let memClsId = 1;

// --- Helpers ---
async function sha256(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2,'0')).join('');
}

function monthPartition(date = new Date()) {
  return `${date.getFullYear()}/${String(date.getMonth()+1).padStart(2,'0')}`;
}

// --- Upload Image ---
export async function uploadPalletImage(file, currentUser) {
  if (!SUPABASE_ENABLED) {
    const hash = await sha256(file);
    const local = { id: memImageId++, original_filename: file.name, storage_path: URL.createObjectURL(file), sha256: hash };
    memory.images.push(local);
    return local;
  }
  const hash = await sha256(file);
  const uuid = crypto.randomUUID();
  const part = monthPartition();
  const ext = file.name.split('.').pop().toLowerCase();
  // Auth user required for prefix isolation
  // Try to capture current user for ownership (nullable if no auth yet)
  let userId = currentUser?.id || null;
  if (!userId) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      userId = userData?.user?.id || null;
    } catch {}
  }
  if (!userId) {
    const err = new Error('Authentication required to upload pallet images (no user session).');
    err.friendly = 'Please sign in before uploading an image.';
    throw err;
  }
  const key = `users/${userId}/raw/${part}/${uuid}_${hash.slice(0,8)}.${ext}`;
  const { error: upErr } = await supabase.storage.from('pallet-images').upload(key, file, { upsert: false });
  if (upErr) throw upErr;
  const insertPayload = { original_filename: file.name, storage_path: key, sha256: hash, uploaded_by: userId };
  const { data, error: insErr } = await supabase.from('pallet_image')
    .insert(insertPayload)
    .select()
    .single();
  if (insErr) {
    // Common RLS failure when no auth user but policy requires uploaded_by = auth.uid()
    if (insErr.message && /RLS|row-level|policy/i.test(insErr.message)) {
      insErr.friendly = 'Upload blocked by Row Level Security. Sign in or relax the insert policy temporarily.';
    }
    throw insErr;
  }
  if (insErr) throw insErr;
  return data;
}

// --- Mock Classification (replace with Edge Function) ---
export async function classifyImage(palletImageId) {
  if (!SUPABASE_ENABLED) {
    const mock = {
      id: memClsId++,
      pallet_image_id: palletImageId,
      model_version: 'mock-local-v1',
      predicted_label: 'pallet_type_A',
      predicted_confidence: 0.87,
      full_json: { mock: true, local: true },
      inference_ms: 100,
      created_at: new Date().toISOString(),
    };
    memory.classifications.unshift(mock);
    return mock;
  }
  try {
    const { data, error } = await supabase.functions.invoke('classify-pallet', { body: { imageId: palletImageId } });
    if (error) throw error;
    return data.classification;
  } catch (e) {
    // Instead of silently inserting a mock row, propagate a structured error so UI can notify user.
    const err = new Error('Classification failed');
    err.cause = e;
    // Provide original message for debugging
    err.details = e?.message || e;
    err.friendly = 'Kunde inte nå klassificeringsfunktionen. Försök igen eller kontrollera loggar.';
    throw err;
  }
}

// --- List History ---
export async function listHistory({ q, label, from, to, minConf, maxConf, page=1, pageSize=10 }) {
  if (!SUPABASE_ENABLED) {
    let cls = [...memory.classifications].map(c => ({
      id: c.id,
      imageUrl: memory.images.find(i => i.id === c.pallet_image_id)?.storage_path,
      name: memory.images.find(i => i.id === c.pallet_image_id)?.original_filename || 'Unnamed',
      label: c.predicted_label,
      confidence: c.predicted_confidence,
      model: c.model_version,
      raw: c.full_json,
      inference_ms: c.inference_ms,
      createdAt: c.created_at,
    }));
    if (q) {
      const ql = q.toLowerCase();
      cls = cls.filter(r => r.label?.toLowerCase().includes(ql) || r.name?.toLowerCase().includes(ql));
    }
    if (label) cls = cls.filter(r => r.label === label);
    if (minConf) cls = cls.filter(r => r.confidence >= parseFloat(minConf));
    if (maxConf) cls = cls.filter(r => r.confidence <= parseFloat(maxConf));
    // from/to ignored in mock for brevity, could implement
    const total = cls.length;
    const start = (page - 1) * pageSize;
    const items = cls.slice(start, start + pageSize);
    return { items, total };
  }
  let query = supabase
    .from('pallet_classification')
    .select('id,pallet_image_id,model_version,predicted_label,predicted_confidence,extracted_name,material,lasttyp,forpackning,stapelbarhet,trycktalighet,viktkategori,omtalighet,form,recommended_action,training_tip,inference_ms,created_at,full_json,pallet_image: pallet_image_id (storage_path, original_filename)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page-1)*pageSize, (page*pageSize)-1);
  if (label) query = query.eq('predicted_label', label);
  if (minConf) query = query.gte('predicted_confidence', minConf);
  if (maxConf) query = query.lte('predicted_confidence', maxConf);
  if (from) query = query.gte('created_at', from);
  if (to) query = query.lte('created_at', to + 'T23:59:59');
  if (q) query = query.or(`predicted_label.ilike.%${q}%,model_version.ilike.%${q}%`);
  const { data, error, count } = await query;
  if (error) throw error;
  const items = await Promise.all((data || []).map(async row => {
    const key = row.pallet_image.storage_path;
    const { data: signed } = await supabase.storage.from('pallet-images').createSignedUrl(key, 60 * 60);
    return {
      id: row.id,
      imageUrl: signed?.signedUrl,
      name: row.pallet_image.original_filename || 'Unnamed',
      label: row.predicted_label,
      extracted_name: row.extracted_name || null,
      confidence: row.predicted_confidence,
      model: row.model_version,
      raw: row.full_json,
      inference_ms: row.inference_ms,
      createdAt: row.created_at,
      // Structured fields
      material: row.material || null,
      lasttyp: row.lasttyp || null,
      forpackning: row.forpackning || null,
      stapelbarhet: row.stapelbarhet || null,
      trycktalighet: row.trycktalighet || null,
      viktkategori: row.viktkategori || null,
      omtalighet: row.omtalighet || null,
      form: row.form || null,
      recommended_action: row.recommended_action || null,
      training_tip: row.training_tip || null,
    };
  }));
  return { items, total: count || 0 };
}

// --- Delete Classification ---
export async function deleteClassificationRecord(id) {
  if (!SUPABASE_ENABLED) {
    const idx = memory.classifications.findIndex(c => c.id === id);
    if (idx >= 0) memory.classifications.splice(idx, 1);
    return true;
  }
  const { error } = await supabase.from('pallet_classification').delete().eq('id', id);
  if (error) throw error;
  return true;
}

// Convenience adapter so HistoryTable props remain unchanged
export async function fetchHistoryPageAdapter(params) {
  return listHistory(params);
}
